import { QrCode } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Flex,
  HeaderItalic,
  MenuLinkButton,
  ScrollArea,
} from '@/components/common';
import { ShareProfileDialog } from '@/components/profile';
import { useCurrentUser } from '@/contexts';
import { authService } from '@/services';
import { calculateAge } from '@/types';
import { firstNameOf, firstPhotoUrl } from '@/utils';

const ACCOUNT_NAV = [
  { to: '/account/my-profile', labelKey: 'account.myProfile' as const },
  { to: '/account/referral', labelKey: 'account.referral' as const },
  { to: '/account/settings', labelKey: 'account.settings' as const },
  { to: '/account/subscription', labelKey: 'account.subscription' as const },
  { to: '/account/help-legal', labelKey: 'account.helpLegal' as const },
];

export function AccountPage() {
  const { t } = useTranslation('profile');
  const { isAdmin, currentUser: profile } = useCurrentUser();

  if (!profile) return null;

  const name = profile.name || '';
  const firstName = firstNameOf(name);
  const age = profile.age ?? calculateAge(profile.birthday);
  const photoUrl = firstPhotoUrl(profile.photos);

  return (
    <ScrollArea>
      <Flex direction="column" align="center" gap="5" py="5" px="3">
        <Box position="relative">
          <Avatar name={name} src={photoUrl} size="9" radius="full" border />
          <Box position="absolute" bottom="0" right="0">
            <ShareProfileDialog
              uid={profile.uid}
              name={firstName}
              icon={<QrCode size={16} aria-hidden />}
              triggerVariant="solid"
              triggerRadius="large"
            />
          </Box>
        </Box>

        <HeaderItalic align="center">{t('account.nameAge', { name, age })}</HeaderItalic>

        <Flex direction="column" gap="3" width="100%" mt="2">
          {ACCOUNT_NAV.map(({ to, labelKey }) => (
            <MenuLinkButton
              key={to}
              to={to}
              title={t(labelKey)}
              as="heading"
              variant="classic"
              data-testid={to.replace(/\//g, '-').slice(1)}
            />
          ))}
          {isAdmin && import.meta.env.VITE_ADMIN_URL && (
            <MenuLinkButton
              to={import.meta.env.VITE_ADMIN_URL}
              title={t('account.adminPanel')}
              external
              as="heading"
              variant="classic"
              data-testid="admin-panel"
            />
          )}
        </Flex>

        <Button
          type="button"
          variant="outline"
          color="crimson"
          fullWidth
          size="lg"
          mt="4"
          onClick={() => void authService.signOut()}
        >
          {t('account.logOut')}
        </Button>
      </Flex>
    </ScrollArea>
  );
}
